/**
 * Self-registration for patients and doctors.
 *
 * Merges the patient signup branch of `handlePatientAuth` with
 * `handleDoctorRegistration`. Both hit POST /api/auth/register — the backend
 * accepts exactly these two roles and marks a new doctor `pending`, which is
 * what the doctor path tells the user to expect.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Field,
  Icon,
  Input,
  Panel,
  Select,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { validatePatientSignup, validateDoctorRegistration } from '../../domain/validation';
import { readFileAsDataUri, compressImage } from '../../domain/files';

const ACCOUNT_TYPES = [
  { value: 'patient', label: 'مستخدم / مريض', icon: 'user' },
  { value: 'doctor', label: 'طبيب', icon: 'stethoscope' },
];

const EMPTY_FORM = {
  name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  categoryId: '',
  title: '',
  priceExam: 500,
  priceFollowup: 200,
};

export function RegisterPage() {
  const { register } = useAuth();
  const { categories } = useData();
  const navigate = useNavigate();

  const [role, setRole] = useState('patient');
  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const isDoctor = role === 'doctor';

  const update = (field) => (event) => {
    const value = event?.target?.type === 'number' ? event.target.value : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setServerError(null);
  };

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      // Profile photos go into the DB as base64, so they are compressed first.
      const dataUri = file.type.startsWith('image/')
        ? await compressImage(file, { maxWidth: 600, maxHeight: 600, quality: 0.75 })
        : await readFileAsDataUri(file);
      setImage(dataUri);
    } catch (err) {
      setServerError(`تعذر قراءة الصورة: ${err.message}`);
    }
  };

  const validation = useMemo(
    () => (isDoctor ? validateDoctorRegistration : validatePatientSignup),
    [isDoctor],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = validation(form);
    setErrors(result.errors);
    if (!result.valid) return;

    setSubmitting(true);
    setServerError(null);

    try {
      const payload = {
        name: form.name.trim(),
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim(),
        phone: form.phone.trim(),
        role,
      };

      if (isDoctor) {
        Object.assign(payload, {
          categoryId: form.categoryId,
          title: form.title.trim() || 'طبيب',
          priceExam: Number(form.priceExam) || 500,
          priceFollowup: Number(form.priceFollowup) || 200,
          image,
        });
      }

      await register(payload);

      setDone(
        isDoctor
          ? 'تم إرسال طلب التسجيل بنجاح. سيتم تفعيل حسابك بعد موافقة الإدارة.'
          : 'تم إنشاء الحساب بنجاح. يمكنك تسجيل الدخول الآن.',
      );

      if (!isDoctor) setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setServerError(err.message || 'حدث خطأ أثناء التسجيل');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center app-backdrop p-4">
        <Panel className="w-full max-w-md text-center">
          <Icon name="check-circle-2" size={48} className="mx-auto mb-4 text-emerald-400" />
          <h2 className="mb-2 text-lg font-bold text-white">تم بنجاح</h2>
          <p className="mb-6 text-sm leading-relaxed text-slate-300">{done}</p>
          <Link to="/login" className="btn-primary mx-auto w-fit">
            الذهاب لتسجيل الدخول
          </Link>
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center app-backdrop p-4">
      <div className="w-full max-w-lg py-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-400">
            <Icon name="user-plus" size={28} />
          </div>
          <h1 className="text-xl font-bold text-white">إنشاء حساب جديد</h1>
        </div>

        <Panel>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Account type */}
            <div className="grid grid-cols-2 gap-2">
              {ACCOUNT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => {
                    setRole(type.value);
                    setErrors({});
                  }}
                  className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-bold transition-colors ${
                    role === type.value
                      ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                      : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Icon name={type.icon} size={16} />
                  {type.label}
                </button>
              ))}
            </div>

            <Field label="الاسم بالكامل" error={errors.name} required>
              <Input value={form.name} onChange={update('name')} placeholder="الاسم" />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="اسم المستخدم" error={errors.username} required>
                <Input value={form.username} onChange={update('username')} autoComplete="username" />
              </Field>

              <Field label="كلمة المرور" error={errors.password} required>
                <Input
                  type="password"
                  value={form.password}
                  onChange={update('password')}
                  autoComplete="new-password"
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="البريد الإلكتروني"
                error={errors.email}
                required={!isDoctor}
                hint={!isDoctor ? 'يجب أن يكون بريد Gmail' : undefined}
              >
                <Input
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  dir="ltr"
                  placeholder="name@gmail.com"
                />
              </Field>

              <Field label="رقم الهاتف" error={errors.phone}>
                <Input value={form.phone} onChange={update('phone')} dir="ltr" placeholder="01xxxxxxxxx" />
              </Field>
            </div>

            {isDoctor && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="التخصص" error={errors.categoryId} required>
                    <Select value={form.categoryId} onChange={update('categoryId')}>
                      <option value="">اختر التخصص</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="اللقب المهني" hint="مثال: استشاري جراحة عامة">
                    <Input value={form.title} onChange={update('title')} placeholder="طبيب" />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="سعر الكشف" error={errors.priceExam}>
                    <Input type="number" min="0" value={form.priceExam} onChange={update('priceExam')} />
                  </Field>

                  <Field label="سعر المتابعة" error={errors.priceFollowup}>
                    <Input
                      type="number"
                      min="0"
                      value={form.priceFollowup}
                      onChange={update('priceFollowup')}
                    />
                  </Field>
                </div>

                <Field label="الصورة الشخصية">
                  <div className="flex items-center gap-3">
                    {image && (
                      <img
                        src={image}
                        alt="معاينة"
                        className="h-14 w-14 rounded-xl border border-white/10 object-cover"
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImage}
                      className="flex-1 text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
                    />
                  </div>
                </Field>

                <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-200">
                  <Icon name="info" size={13} className="ml-1 inline" />
                  حسابات الأطباء تحتاج موافقة الإدارة قبل التفعيل. سيتم إنشاء عيادة رئيسية
                  تلقائياً يمكنك تعديلها بعد الموافقة.
                </p>
              </>
            )}

            {serverError && (
              <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-300">
                <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0" />
                <span>{serverError}</span>
              </div>
            )}

            <Button type="submit" className="w-full" loading={submitting} icon="user-plus">
              إنشاء الحساب
            </Button>

            <p className="text-center text-xs text-slate-400">
              لديك حساب بالفعل؟{' '}
              <Link to="/login" className="font-bold text-cyan-400 hover:text-cyan-300">
                تسجيل الدخول
              </Link>
            </p>
          </form>
        </Panel>
      </div>
    </div>
  );
}

export default RegisterPage;
